/**
 * eslint-disable @typescript-eslint/indent
 *
 * @format
 */

/** @format */

import {colors} from '../styles/js/colors';

export type Package = {
	stripeProdIds?: Array<{
		// prettier-ignore
		name:
		| 'membership'
		| 'joiningFee'
		| 'marketing'
		| 'additionalProperty'
		| 'additionalWebpage';
		productId: string;
		priceId: string;
	}>;
	cmsContent: {
		topCard: {
			text: {
				overline?: string;
				heading: string;
				body: string;
			};
			backgroundColor: string;
		};
		bullets: string[];
		pricing?: {
			joiningFee: number;
			annualFee: number;
		};
		marketingPackageAccordion?: {
			packageInfo: string;
			bullets: string[];
			pricing: {
				annualFee: number;
				propertyFee: number;
				webpageFee: number;
			};
		};
		memberPackage: string;
		textSections?: Array<{heading: string; body: string}>;
		url?: string;
		cta?: {title: string; url: string};
	};
};

export type PaymentMethod = {
	id: string;
	label: string;
	name: string;
	instructions: string;
	details: string[];
};

type HeroBannerText = {
	heading: string;
	supportingText?: string;
	body?: string;
};

type TooltipData = {
	tooltip: string[];
};

type ContentData = {
	upgradeMembership: HeroBannerText;
	manageMembership: HeroBannerText;
	lovedByLocals: HeroBannerText;
	login: HeroBannerText;
	editPassword: HeroBannerText;
	invoices: HeroBannerText;
	protectedContent: any;
	passwordResetInstruction: string;
	packages: Package[];
	paymentMethods: PaymentMethod[];
	usernameOnRegister: TooltipData;
	additionalProperty: TooltipData;
	additionalWebpage: TooltipData;
};

const contentData: ContentData = {
	upgradeMembership: {
		heading: 'Upgrade your ASSC membership',
		supportingText:
			'Take your ASSC membership to the next level, with a bespoke marketing package.',
	},
	manageMembership: {
		heading: 'Manage your ASSC membership',
		supportingText:
			'See what’s included in your membership and upgrade if you’re an owner, manager or agency.',
	},
	usernameOnRegister: {
		tooltip: [
			// prettier-ignore
			'Choosing a username helps personalise your profile as an ASSC member and keeps our site as secure as possible.',
			'Please ensure your username contains no spaces and is easy to remember.',
		],
	},
	additionalProperty: {
		tooltip: [
			'This is for members who own and operate multiple properties within a self-catering business. Adding additional properties allows the benefits of your marketing add-on to be applied to more than one property.',
			'If you operate three properties, for example, you will need to add two additional properties. This does not mean you need to add two additional webpages, but if you choose not to, all of your properties will need to be displayed on one EmbraceScotland listing. This will likely result in less interest and engagement from website users.',
		],
	},
	additionalWebpage: {
		tooltip: [
			'This is for members who want to display and promote distinct, separate property listings on EmbraceScotland, for the different properties they operate. It is also applicable and necessary for members looking to appear in search results for two different region categories.',
			'If you have three properties that span across an estate, for example, with distinct identities, facilities and price points, you can add two additional property webpages (as well as two additional properties above) to advertise these properties separately on EmbraceScotland.',
		],
	},
	lovedByLocals: {
		heading: 'Submit a tip',
		supportingText: 'Tell us about a Loved By Locals tip.',
	},
	login: {
		// prettier-ignore
		heading: 'Your Members Area',
		supportingText:
			'Here, members can manage their membership and access news, resources, and the ASSC discussion forum.',
	},
	editPassword: {
		// prettier-ignore
		heading: 'Change your password',
		supportingText:
			'Update the password associated with your ASSC member account.',
	},
	invoices: {
		heading: 'ASSC Invoices',
		supportingText:
			'View and download your invoices here, which can be paid by card.',
	},
	protectedContent: {
		post: {
			variant: 'left',
			isFullBleed: true,

			heading: 'You have to be a member to view this',
			text: 'To access this content, log in to your ASSC account or join today.',
			backgroundColor: colors.tweed[400],
			ctas: [
				{
					buttonType: 'primary',
					isLink: true,
					title: 'Become a member',
					link: {url: '/join-the-assc', title: 'Become a member'},
				},
			],
		},
	},
	passwordResetInstruction:
		'Please enter the email address associated with your account. We’ll send you instructions to reset your password.',
	packages: [
		{
			cmsContent: {
				topCard: {
					text: {
						heading: 'Owners + Managers',
						body: 'Benefit from a wide range of support to boost bookings and reduce expenses.',
					},
					backgroundColor: colors.lime[400],
				},
				bullets: [
					'Industry-leading advice, guidance, advocacy and representation',
					'Events and networking opportunities with vibrant member community',
					'Discounts from trade suppliers and business support from industry experts',
				],
				pricing: {joiningFee: 5000, annualFee: 14500},
				marketingPackageAccordion: {
					packageInfo:
						'Our marketing package for owners and managers aims to bring more visitor enquires and direct bookings to your business.',
					bullets: [
						'List a property page on high-ranking commission-free booking website, EmbraceScotland',
						'Set up additional property listing pages as required for your self-catering business',
						'Participate in extensive digital marketing campaigns on search engines and social media',
						'Use the ASSC and EmbraceScotland logos in your own advertising and branding materials',
					],
					pricing: {
						annualFee: 6900,
						propertyFee: 1600,
						webpageFee: 2600,
					},
				},
				memberPackage: 'owners-managers',
			},
		},
		{
			cmsContent: {
				topCard: {
					text: {
						heading: 'Agencies',
						body: 'Manage a portfolio of self-catering properties with guidance and representation at your disposal.',
					},
					backgroundColor: colors.aquaGreen[400],
				},
				bullets: [
					'Industry-wide credibility of membership in official trade association',
					'Opportunities to network with members and hold dialogue with authorities',
					'Regular policy and news updates to support management of portfolio',
				],
				pricing: {joiningFee: 9000, annualFee: 21000},
				marketingPackageAccordion: {
					packageInfo:
						'Our marketing package for agencies facilitates the wider promotion of your portfolio.',
					bullets: [
						'List a property page on high-ranking commission-free booking website, EmbraceScotland',
						'Put your portfolio front-and-centre in regular far-reaching email marketing & write-ups',
						'Participate in extensive digital marketing campaigns on search engines and social media',
						'Use the ASSC and EmbraceScotland logos in your own advertising and branding materials',
					],
					pricing: {
						annualFee: 6900,
						propertyFee: 1600,
						webpageFee: 2600,
					},
				},
				memberPackage: 'agencies',
			},
		},
		{
			cmsContent: {
				topCard: {
					text: {
						heading: 'Trade Suppliers',
						body: 'Receive endorsement and exposure from Scotland’s official self-catering trade association.',
					},
					backgroundColor: colors.moss[400],
				},
				bullets: [
					'Listing as an official trade supplier in the ASSC member’s area',
					'Business recommendation made to over 1,700 self-caterers',
					'Marketing opportunities and regular updates on industry news',
				],
				pricing: {joiningFee: 9000, annualFee: 25000},
				memberPackage: 'trade-suppliers',
			},
		},
		{
			cmsContent: {
				topCard: {
					text: {
						overline: 'ASSC...',
						heading: 'Partners',
						body: 'Market your business with an enhanced partnership, connecting you directly to our community.',
					},
					backgroundColor: colors.sage[400],
				},
				bullets: [''],
				// pricing: {},
				// marketingPackageAccordion: {
				// 	packageInfo: '',
				// 	bullets: [''],
				// 	pricing: {},
				// },
				textSections: [
					{
						heading: 'Trade Supplier Plus',
						body: 'Our Trade Supplier Plus programme enables you to expand your reach and impact as a supplier by engaging directly with members of the ASSC.',
					},
					{
						heading: 'Partners for Business ',
						body: 'Our Partners for Business Scheme is open to industry experts hoping to maximise exposure for their business and contribute to the work of the ASSC.',
					},
				],
				cta: {title: 'Enquire today', url: '/join-the-assc/partnership'},
				memberPackage: 'partners',
			},
		},
	],
	paymentMethods: [
		{
			id: '0',
			label: 'Stripe',
			name: 'stripe',
			instructions: '',
			details: [],
		},
		// {
		// 	id: '1',
		// 	label: 'Cheque',
		// 	name: 'cheque',
		// 	instructions: 'Please send cheque payment to the address below:',
		// 	details: [
		// 		'Administrator and Membership Secretary',
		// 		'Mansefield House',
		// 		'Arrochar G83 7AG',
		// 	],
		// },
		{
			id: '2',
			label: 'BACS',
			name: 'banktransfer',
			instructions: 'Please find details below to make a BACS payment:',
			details: [
				'Sort code: 50-89-56 ',
				'Account No.: 678906679',
				'Reference: Please use your full name',
			],
		},
	],
};

export default contentData;
